/**
 * Logo — inline `><>` brand mark (no asset needed).
 *
 * Product variant renders the fish mark + "phishiness"; the `dl` variant
 * swaps to the `[DL] cyber security` umbrella mark. Rebuilt inline in JSX
 * (JetBrains Mono, brackets/fish in terminal green) per the brand handoff.
 */

type LogoVariant = 'product' | 'dl';
type LogoSize = 'sm' | 'md' | 'lg';

interface LogoProps {
  /** Which mark to render */
  variant?: LogoVariant;
  /** Size variant */
  size?: LogoSize;
  /** Additional CSS classes */
  className?: string;
}

const sizeMap: Record<LogoSize, { mark: string; word: string }> = {
  sm: { mark: 'text-[16px]', word: 'text-[14px]' },
  md: { mark: 'text-[20px]', word: 'text-[17px]' },
  lg: { mark: 'text-[26px]', word: 'text-[22px]' },
};

/**
 * Phishiness / [DL] inline logo lockup.
 */
export function Logo({ variant = 'product', size = 'md', className = '' }: LogoProps) {
  const { mark, word } = sizeMap[size];

  if (variant === 'dl') {
    return (
      <span className={`flex items-center gap-2 font-mono ${className}`}>
        <span className={`${mark} font-bold tracking-tighter`}>
          <span className="text-phish-accent">[</span>
          <span className="text-phish-fg-1">DL</span>
          <span className="text-phish-accent">]</span>
        </span>
        <span className={`${word} font-medium text-phish-fg-2`}>cyber security</span>
      </span>
    );
  }

  return (
    <span className={`flex items-center gap-2 font-mono ${className}`}>
      <span className={`${mark} font-bold tracking-tighter text-phish-accent`}>&gt;&lt;&gt;</span>
      <span className={`${word} font-medium text-phish-fg-1`}>phishiness</span>
    </span>
  );
}

export default Logo;
